/**
 * Cognito User Pool 認証コンテキスト。
 *
 * - amazon-cognito-identity-js を用いて USER_PASSWORD_AUTH (Password Authentication)
 *   フローでログインする。
 * - ログイン成功時に取得した ID トークンを React state に保持し、API クライアントが
 *   `Authorization: Bearer <idToken>` で利用する。
 * - リフレッシュトークンが localStorage に永続化されるため、ページ再読み込みでも
 *   セッションが復元される (CognitoUserPool.getCurrentUser() で確認)。
 * - セルフサインアップ: signUp → (メール宛コード受信) → confirmSignUp の 2 ステップ。
 *   未確認状態のままログインを試みると Cognito 側で `UserNotConfirmedException` が
 *   返るため、その場合はサインアップ確認画面へ誘導する。
 */
import {
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { AppConfig } from '../config';

export interface AuthState {
  /** 認証済みかどうか */
  isAuthenticated: boolean;
  /** 認証中の判定がまだ完了していない (初期化中)。trueの間はローディング表示する */
  isLoading: boolean;
  /** ログインユーザの email (sub から見つからない場合は username が入る) */
  username: string | null;
  /** API 呼び出し時に Bearer に乗せる ID トークン (期限切れの場合は再取得済み) */
  getIdToken: () => Promise<string>;
  /** ログイン処理 */
  signIn: (email: string, password: string) => Promise<void>;
  /** ログアウト処理 */
  signOut: () => void;
  /** セルフサインアップ: 新規ユーザを作成し、メールアドレスへ確認コードを送信する */
  signUp: (email: string, password: string) => Promise<void>;
  /** メール確認コードを検証し、ユーザを有効化する */
  confirmSignUp: (email: string, code: string) => Promise<void>;
  /** 確認コードの再送信 */
  resendConfirmationCode: (email: string) => Promise<void>;
  /** パスワードのセルフリセット (1): リセットコードをメールへ送信する */
  forgotPassword: (email: string) => Promise<void>;
  /** パスワードのセルフリセット (2): 受信したコードと新パスワードで再設定する */
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  /** 直近のエラーメッセージ */
  error: string | null;
}

const AuthContext = createContext<AuthState | null>(null);

interface AuthProviderProps {
  config: AppConfig;
  children: ReactNode;
}

export function AuthProvider({ config, children }: AuthProviderProps): React.JSX.Element {
  // CognitoUserPool は config から構築。再初期化を防ぐため useMemo で固定化する。
  const userPool = useMemo(
    () =>
      new CognitoUserPool({
        UserPoolId: config.userPoolId,
        ClientId: config.userPoolClientId,
      }),
    [config.userPoolId, config.userPoolClientId],
  );

  const [currentUser, setCurrentUser] = useState<CognitoUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [username, setUsername] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 初期化: localStorage に保存されている既存セッションを復元する。
  useEffect(() => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      setIsLoading(false);
      return;
    }
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        setCurrentUser(null);
        setUsername(null);
      } else {
        setCurrentUser(cognitoUser);
        setUsername(extractUsername(session));
      }
      setIsLoading(false);
    });
  }, [userPool]);

  const signIn = useCallback(
    (email: string, password: string): Promise<void> => {
      setError(null);
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      const authDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      return new Promise<void>((resolve, reject) => {
        cognitoUser.authenticateUser(authDetails, {
          onSuccess: (session) => {
            setCurrentUser(cognitoUser);
            setUsername(extractUsername(session));
            resolve();
          },
          onFailure: (err: Error) => {
            const message = err.message || 'ログインに失敗しました';
            setError(message);
            reject(err);
          },
          newPasswordRequired: () => {
            // 初期パスワード変更が要求された場合は明示的にエラーにする。
            // (本実装では新パスワードを設定する UI は持たないため、運用側で
            // adminSetUserPassword 等で永続パスワードを設定する想定)
            const message =
              'このユーザは初期パスワードのままです。管理者にパスワードを再設定してもらってください。';
            setError(message);
            reject(new Error(message));
          },
        });
      });
    },
    [userPool],
  );

  const signOut = useCallback(() => {
    if (currentUser) {
      currentUser.signOut();
    }
    setCurrentUser(null);
    setUsername(null);
    setError(null);
  }, [currentUser]);

  // セルフサインアップ: 新規ユーザを Cognito に登録する。
  // 成功すると Cognito から指定メールアドレスへ確認コードが送信される。
  const signUp = useCallback(
    (email: string, password: string): Promise<void> => {
      setError(null);
      const attributes = [new CognitoUserAttribute({ Name: 'email', Value: email })];
      return new Promise<void>((resolve, reject) => {
        userPool.signUp(email, password, attributes, [], (err) => {
          if (err) {
            const message = err.message || 'サインアップに失敗しました';
            setError(message);
            reject(err);
            return;
          }
          resolve();
        });
      });
    },
    [userPool],
  );

  // 確認コード検証: メールに届いたコードを送信してユーザを有効化する。
  const confirmSignUp = useCallback(
    (email: string, code: string): Promise<void> => {
      setError(null);
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      return new Promise<void>((resolve, reject) => {
        cognitoUser.confirmRegistration(code, true, (err) => {
          if (err) {
            const message = err.message || '確認コードの検証に失敗しました';
            setError(message);
            reject(err);
            return;
          }
          resolve();
        });
      });
    },
    [userPool],
  );

  // 確認コードを再送する。
  const resendConfirmationCode = useCallback(
    (email: string): Promise<void> => {
      setError(null);
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      return new Promise<void>((resolve, reject) => {
        cognitoUser.resendConfirmationCode((err) => {
          if (err) {
            const message = err.message || '確認コードの再送に失敗しました';
            setError(message);
            reject(err);
            return;
          }
          resolve();
        });
      });
    },
    [userPool],
  );

  // パスワードのセルフリセット (1): リセットコードをメールへ送信する。
  // Cognito の forgotPassword フローを利用する (AccountRecovery=EMAIL_ONLY)。
  const forgotPassword = useCallback(
    (email: string): Promise<void> => {
      setError(null);
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      return new Promise<void>((resolve, reject) => {
        cognitoUser.forgotPassword({
          onSuccess: () => {
            resolve();
          },
          onFailure: (err: Error) => {
            const message = err.message || 'パスワードリセットコードの送信に失敗しました';
            setError(message);
            reject(err);
          },
        });
      });
    },
    [userPool],
  );

  // パスワードのセルフリセット (2): 受信したコードと新パスワードでパスワードを再設定する。
  const confirmForgotPassword = useCallback(
    (email: string, code: string, newPassword: string): Promise<void> => {
      setError(null);
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      return new Promise<void>((resolve, reject) => {
        cognitoUser.confirmPassword(code, newPassword, {
          onSuccess: () => {
            resolve();
          },
          onFailure: (err: Error) => {
            const message = err.message || 'パスワードの再設定に失敗しました';
            setError(message);
            reject(err);
          },
        });
      });
    },
    [userPool],
  );

  // 有効な ID トークンを取得する。期限切れなら refreshToken で更新する。
  const getIdToken = useCallback((): Promise<string> => {
    return new Promise((resolve, reject) => {
      const user = currentUser ?? userPool.getCurrentUser();
      if (!user) {
        reject(new Error('Not authenticated'));
        return;
      }
      user.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          reject(err ?? new Error('Failed to fetch session'));
          return;
        }
        // getSession 自体が refreshToken による自動更新を実施する。
        resolve(session.getIdToken().getJwtToken());
      });
    });
  }, [currentUser, userPool]);

  const value: AuthState = useMemo(
    () => ({
      isAuthenticated: currentUser !== null,
      isLoading,
      username,
      getIdToken,
      signIn,
      signOut,
      signUp,
      confirmSignUp,
      resendConfirmationCode,
      forgotPassword,
      confirmForgotPassword,
      error,
    }),
    [
      currentUser,
      isLoading,
      username,
      getIdToken,
      signIn,
      signOut,
      signUp,
      confirmSignUp,
      resendConfirmationCode,
      forgotPassword,
      confirmForgotPassword,
      error,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Cognito の ID トークンから email (なければ username) を取り出す。 */
function extractUsername(session: CognitoUserSession): string {
  const payload = session.getIdToken().decodePayload();
  return (payload['email'] as string) ?? (payload['cognito:username'] as string) ?? '';
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
